import { UIIcon } from '../config/icons';
import { ABScreen } from '../lib/ab/screen';
import { ABEmailCheck } from '../lib/ab/widgets';
import { aTrackPage } from '../lib/mdl/a-track';
import { cGetDefaultCurrency } from '../lib/mdl/c-country';
import { cPlanReferralDiscount } from '../lib/mdl/c-plan';
import { getOutlookContacts, MEInviteContact, MEInviteModel, meInviteResend, meInvitesGet, meInvitesSend } from '../lib/me/invite';
import { the_user } from '../lib/me/login';
import { getCountry } from '../lib/me/me';
import { UIDataAdapter } from '../lib/ui/component/adapter';
import { UIButton } from '../lib/ui/component/button';
import { UIChipInput } from '../lib/ui/component/chip';
import { UIDialog } from '../lib/ui/component/dialog';
import { UISearchInput, UITextInput } from '../lib/ui/component/input';
import { UIList } from '../lib/ui/component/list';
import { UITable, UITableDefinition } from '../lib/ui/component/table';
import { UICheckbox } from '../lib/ui/component/toggle';
import { addTooltip } from '../lib/ui/component/tooltip';
import { $el } from '../lib/ui/elements';
import { uiReplaceI18N } from '../lib/ui/functions';
import { UIRelPos } from '../lib/ui/types';
import { UIViewSpec } from '../lib/ui/view';
import { $amount } from '../lib/y/amount';
import { $analytics } from '../lib/y/analytics';
import { YArrayDataSource } from '../lib/y/data-source';
import { $on } from '../lib/y/events';
import { $format } from '../lib/y/format';
import { $google } from '../lib/y/google';
import { $i18n, yInterpolate } from '../lib/y/i18n';
import { $microsoft } from '../lib/y/microsoft';
import { $time } from '../lib/y/time';
import { HomeMenu } from './menu';

interface InviteContact extends MEInviteContact {
    selected?: boolean;
}

class InviteDialog extends UIDialog {
    constructor(all_contacts: InviteContact[], source: 'gmail' | 'outlook') {
        super({ close: true, classes: 'm-600 m-mobile-fs' });
        const content = this.content;

        const search = new UISearchInput({ placeholder: 'Sök efter vänner' });

        const def = new UITableDefinition<InviteContact>();
        def.addCustom({
            width: -40, decorator: (td, row) => {
                const contact = row.item;
                const checkbox = new UICheckbox();
                checkbox.value = !!contact.selected;
                td.ui_add(checkbox);
                $on(checkbox, 'value', s => {
                    contact.selected = s;
                });
            }
        });
        def.addValue(i => i.name || '', { width: 100, classes: 't-ellipsize' });
        def.addValue(i => i.email, { width: 100, classes: 't-ellipsize' });

        const table_source = new YArrayDataSource<InviteContact>();

        function doSearch() {
            const contacts = search.filterArray(all_contacts, (c, w) => {
                if(c.email.toLocaleLowerCase().includes(w)) return true;
                if(c.name && c.name.toLocaleLowerCase().includes(w)) return true;
                return false;
            });
            table_source.data = contacts.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        }

        doSearch();
        $on(search, 'value', doSearch);

        content.ui_add($el.DIV('w-block l-form m-center', [
            $el.H2('title', $i18n.tl.h_invite_dialog),
            $el.P('info', $i18n.tl.t_invite_dialog_choose),
            search,
            new UITable(def, table_source, { header: false, classes: 'b' }),
            $el.DIV('l-button-row', [
                new UIButton({
                    title: $i18n.tl.b_invite_dialog_send, on_click: () => {
                        const selected = all_contacts.filter(c => c.selected);
                        if(selected.length) {
                            return meInvitesSend(selected, source).then(() => {
                                this.dismiss(true);
                                return true;
                            });
                        }
                        return;
                    }
                })
            ])
        ]));
    }
}

class InvitePopup extends UIDialog {
    constructor() {
        super({ close: true, classes: 'm-550 m-mobile-fs' });

        const discount = $format.currency(cPlanReferralDiscount());

        this.content.ui_add($el.DIV('w-block m-center', [
            $el.DIV('tl-popup-number', '1'),
            $el.H3('heading', $i18n.tl.h_invite_popup_1),
            $el.P('text', $i18n.tl.t_invite_popup_1),
            $el.DIV('s-section'),
            $el.DIV('tl-popup-number', '2'),
            $el.H3('heading', $i18n.tl.h_invite_popup_2),
            $el.P('text', $i18n.tl.t_invite_popup_2),
            $el.DIV('s-section'),
            $el.DIV('tl-popup-number', '3'),
            $el.H3('heading', yInterpolate($i18n.tl.h_invite_popup_3, discount)),
            $el.P('text', yInterpolate($i18n.tl.t_invite_popup_3, discount)),
            $el.DIV('s-section'),
        ]));
    }
}

export class HomeInviteScreen extends ABScreen {
    constructor() {
        $google.prepare();

        super({ menu: new HomeMenu({ classes: 'm-pink c-light' }) });
    }

    protected override fillContent(content: UIViewSpec): void {
        const invite_code = the_user!.invite_code;
        const share_url = (getCountry() == 'DE' ? 'https://abundolive.de/?code=' : 'https://abundolive.se/?code=') + invite_code;
        const link = new UITextInput({ disabled: true });
        link.value = share_url;

        const emails = new UIChipInput({ placeholder: $i18n.tl.ph_invite_emails, space_break: true });

        const code = new UITextInput({ disabled: true });
        code.value = invite_code;

        emails.ui_add(new ABEmailCheck(emails, true));

        const invite_list = $el.DIV('i-column m-540 l-form');

        content.ui_add([
            $el.DIV('ui-section pad-t-none pad-b-none', [
                $el.DIV('tl-invite-banner c-light', [
                    $el.DIV('r-title', uiReplaceI18N($i18n.tl.h_invite_reward, $el.SPAN('t-c-tint', $format.currency(cPlanReferralDiscount())))),
                    $el.DIV('r-image')
                ]),
            ]),
            $el.DIV('ui-section l-columns w-block', [
                $el.DIV('i-column m-540 l-form', [
                    $el.H3('heading', $i18n.tl.h_invite_link),
                    $el.DIV('l-input-row', [
                        link,
                        new UIButton({
                            title: $i18n.tl.b_copy_link, classes: 'ui-tint-reverse', on_click: () => {
                                navigator.clipboard.writeText(share_url);
                            }
                        })
                    ]),
                    $el.DIV('s-medium'),
                    $el.H3('heading', $i18n.tl.h_invite_email),
                    $el.P('input-hint', $i18n.tl.t_invite_email),
                    $el.DIV('l-input-row', [
                        emails,
                        new UIButton({
                            title: $i18n.tl.b_send, classes: 'ui-tint-reverse', on_click: () => {
                                emails.commit();
                                const list = emails.value.map(e => ({ email: e }));
                                if(list.length) {
                                    return meInvitesSend(list, 'field').then(() => {
                                        emails.value = [];
                                        return true;
                                    });
                                }
                                return;
                            }
                        })
                    ]),
                    $el.P('input-hint', [
                        $i18n.tl.t_invite_choose_from + ' ',
                        new UIButton({
                            kind: 'k-text', title: 'Gmail', classes: 'm-inline', on_click: () => {
                                $google.auth().then(() => {
                                    $google.getContacts().then(contacts => {
                                        new InviteDialog(contacts, 'gmail').present();
                                    });
                                });
                            }
                        }),
                        ' ' + $i18n.tl.t_invite_or + ' ',
                        new UIButton({
                            kind: 'k-text', title: 'Outlook', classes: 'm-inline', on_click: () => {
                                $microsoft.auth2().then(token => {
                                    getOutlookContacts(token).then(contacts => {
                                        new InviteDialog(contacts, 'outlook').present();
                                    });
                                });
                            }
                        })
                    ]),
                    $el.DIV('s-medium'),
                    $el.H3('heading', $i18n.tl.t_invite_code),
                    $el.P('input-hint', $i18n.tl.t_invite_code_info),
                    $el.DIV('l-input-row', [
                        code,
                        new UIButton({
                            title: $i18n.tl.b_copy_code, classes: 'ui-tint-reverse', on_click: () => {
                                navigator.clipboard.writeText(invite_code);
                            }
                        })
                    ]),
                    $el.DIV('s-small'),
                    new UIButton({
                        title: $i18n.tl.b_how_it_works, kind: 'k-text', on_click: () => {
                            new InvitePopup().present();
                        }
                    })
                ]),
                invite_list
            ])
        ]);

        meInvitesGet().then(invites => {
            if(invites.length) {
                invites.sort((a, b) => $time(b.created_at).n - $time(a.created_at).n);
                const saved = invites.reduce((a, b) => $amount.add(a, b.discount), $amount.zero(cGetDefaultCurrency()));

                invite_list.ui_add([
                    $el.H3('heading', $i18n.tl.h_invited),
                    $el.B($i18n.tl.t_invited_saved + ' '),
                    $el.DIV('tl-big-number', $format.currency(saved)),
                    $el.DIV('s-medium'),
                    new UIList(new UIDataAdapter((i: MEInviteModel) => {
                        const row = $el.DIV('tl-invite-row', [
                            $el.SPAN('r-name', i.contact.name || i.contact.email),
                            $el.S(),
                            $el.SPAN('r-status', i.subscribed ? $i18n.tl.td_invite_accepted : $i18n.tl.td_invite_sent)
                        ]);

                        if(!i.resent && !i.subscribed) {
                            const resend = new UIButton({
                                classes: 'm-inline', icon: UIIcon.SHARE, on_click: () =>
                                    meInviteResend(i.id).then(() => {
                                        resend.ui_hide();
                                    })
                            });
                            addTooltip($i18n.tl.b_invited_resend, row, { kind: 'k-table', hover: resend, position: UIRelPos.Right });
                            row.ui_add(resend);
                        }

                        return row;
                    }, invites), { classes: 'l-v r-items' })
                ]);
            }
        });

        $analytics.track('Viewed invite page');
        aTrackPage('invite');
    }
}